import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as BlockbenLogo } from "./../assets/icons/logo/blockben-logo-dark.svg";
import {
  SidebarWrapper,
  CompanyInfoCard,
  Label,
  Text,
  StatusDescription,
  SidebarContainer,
  ArrowIcon,
  ContextMenuButton,
  LanguageSelectorWrapper,
  MainContent,
  MainContentInner,
  ArrowButton,
  CloseIconWrapper,
  EmptyStateWrapper,
  EmptyStateText,
} from "./RegisterCompany.elements";
import ContactModalContent from "../components/ContactModalContent";
import { showModal } from "../redux/modal";
import { ReactComponent as ArrowCloseIcon } from "../assets/icons/double_arrow_left.svg";
import { ReactComponent as ContextMenuIcon } from "../assets/icons/context_menu_icon.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/close.svg";
import { ReactComponent as GenericErrorIcon } from "../assets/icons/generic_error.svg";
import { ReactComponent as GenericWarningIcon } from "../assets/icons/generic_warning.svg";
import { ReactComponent as GenericSuccessIcon } from "../assets/icons/generic_success.svg";
import LanguageSelector from "../components/LanguageSelector/LanguageSelector";
import SumsubWebSdk from "@sumsub/websdk-react";
import { useEffect } from "react";
import { ButtonPrimary } from "../components/Button/Button";
import { legalUserActions, legalUserSelectors } from "../redux/legalUser";
import { useLocation } from "react-router-dom";
import { DispatchType } from "../redux";
import ErrorModalContent from "../components/ErrorModalContent";

export enum CompanyStatus {
  NotStarted = "notStarted",
  InProgress = "inProgress",
  Error = "error",
  Done = "done",
}

export enum UserStateMachineStates {
  Created = "Created",
  OnBoarding = "OnBoarding",
  InProgress = "InProgress",
  Approved = "Approved",
  Denied = "Denied",
  FinalDenied = "FinalDenied",
}

export default function RegisterCompany() {
  const [open, setOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  const dispatch = useDispatch<DispatchType>();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const registrationData = useSelector(legalUserSelectors.getRegistrationData);
  const loading = useSelector(legalUserSelectors.getLoading);
  const accessToken = useSelector(legalUserSelectors.getAccessToken);
  const identificationLoading = useSelector(
    legalUserSelectors.getIdentificationLoading
  );

  const urlParams = new URLSearchParams(location.search);
  const registrationId = urlParams.get("company");

  useEffect(() => {
    if (registrationId && !registrationData) {
      dispatch(legalUserActions.fetchLegalUserRegistration(registrationId))
        .unwrap()
        .then((data) => {
          if (
            data.state !== UserStateMachineStates.FinalDenied &&
            data.state !== UserStateMachineStates.Approved
          ) {
            handleGetAccessToken(data.details.registrationId);
          }
        })
        .catch((error) => {
          dispatch(
            showModal({
              title: t("common.error.title"),
              content: <ErrorModalContent message={error} />,
            })
          );
        });
    }
  }, [dispatch, registrationId, registrationData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const rootDiv = document.getElementById("root");
    if (rootDiv) {
      if (open && isMobile) {
        rootDiv.style.overflow = "hidden";
      } else {
        rootDiv.style.overflow = "auto";
      }
    }
    return () => {
      if (rootDiv) rootDiv.style.overflow = "auto";
    };
  }, [open, isMobile]);

  const handleGetAccessToken = async (registrationId: string) => {
    try {
      const result = await dispatch(
        legalUserActions.initiateIdentification(registrationId)
      ).unwrap();
      return result.result.token;
    } catch (error) {
      console.error("Failed to get access token:", error);
      throw error;
    }
  };

  const handleQuestionClick = () => {
    dispatch(
      showModal({
        title: t("companyRegistration.title"),
        content: <ContactModalContent caseNumber={registrationData?.details?.registrationId} />,
      })
    );
  };

  const sidebarWidth = 400;

  // Use registration data or fallback to loading/error states
  const companyInfo = {
    registrationNumber: registrationData?.details?.registrationNumber || "-",
    name: registrationData?.displayText || "-",
    taxIdNumber: registrationData?.details?.taxIdNumber || "-",
    status: registrationData?.state
      ? t(
          `companyRegistration.status.${registrationData?.state}`,
          registrationData?.state
        )
      : "-",
    statusDescription: registrationData?.state
      ? t(`companyRegistration.statusDescription.${registrationData?.state}`)
      : "-",
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case UserStateMachineStates.Approved:
        return <GenericSuccessIcon style={{ width: 96, height: 96 }} />;
      case UserStateMachineStates.Denied:
      case UserStateMachineStates.FinalDenied:
        return <GenericErrorIcon style={{ width: 96, height: 96 }} />;
      case UserStateMachineStates.Created:
        return <GenericWarningIcon style={{ width: 96, height: 96 }} />;
      case UserStateMachineStates.OnBoarding:
        return <GenericWarningIcon style={{ width: 96, height: 96 }} />;
      case UserStateMachineStates.InProgress:
        return <GenericWarningIcon style={{ width: 96, height: 96 }} />;
      default:
        return <GenericErrorIcon style={{ width: 96, height: 96 }} />;
    }
  };

  return (
    <div style={{ display: "flex" }}>
      {/* Sidebar */}
      <SidebarContainer open={open} width={sidebarWidth}>
        <SidebarWrapper
          style={{
            opacity: open ? 1 : 0,
            transitionDelay: open ? "0.15s" : "0s",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <BlockbenLogo
              style={{
                marginBottom: 40,
                width: 169,
                minWidth: 169,
                height: 24,
                minHeight: 24,
              }}
            />
            <CloseIconWrapper onClick={() => setOpen(false)}>
              <CloseIcon />
            </CloseIconWrapper>
          </div>
          <Label>{t("companyRegistration.title")}</Label>
          <CompanyInfoCard>
            <div>
              <Label>{t("companyRegistration.registrationNumber")}</Label>
              <Text as="span">{companyInfo.registrationNumber}</Text>
            </div>
            <div>
              <Label>{t("companyRegistration.name")}</Label>
              <Text as="span">{companyInfo.name}</Text>
            </div>
            <div>
              <Label>{t("companyRegistration.taxIdNumber")}</Label>
              <Text as="span">{companyInfo.taxIdNumber}</Text>
            </div>
            <div>
              <Label>{t("companyRegistration.status.label")}</Label>
              <Text as="span">{companyInfo.status}</Text>
            </div>
            <div>
              <Label>{t("companyRegistration.statusDescription.label")}</Label>
              <StatusDescription>
                {companyInfo.statusDescription}
              </StatusDescription>
            </div>
          </CompanyInfoCard>
          <ButtonPrimary
            style={{ marginTop: "auto", width: "100%" }}
            onClick={handleQuestionClick}
          >
            {t("companyRegistration.questionButton")}
          </ButtonPrimary>
        </SidebarWrapper>
      </SidebarContainer>

      <MainContent open={open} width={sidebarWidth}>
        <ArrowButton open={open} onClick={() => setOpen(!open)}>
          <ArrowIcon open={open}>
            <ArrowCloseIcon />
          </ArrowIcon>
        </ArrowButton>
        <ContextMenuButton onClick={() => setOpen(true)}>
          <ContextMenuIcon />
        </ContextMenuButton>
        <LanguageSelectorWrapper>
          <LanguageSelector />
        </LanguageSelectorWrapper>
        <MainContentInner>
          {loading || identificationLoading ? (
            <></>
          ) : registrationData &&
            registrationData.state !== UserStateMachineStates.FinalDenied &&
            registrationData.state !== UserStateMachineStates.Approved ? (
            <SumsubWebSdk
              accessToken={accessToken!}
              expirationHandler={() =>
                handleGetAccessToken(registrationData!.details.registrationId)
              }
              config={{
                lang: i18n.language,
                theme: "light",
              }}
              options={{
                addViewportTag: false,
                adaptIframeHeight: true,
              }}
              onError={(e: any) => console.error(e)}
              onMessage={(type: any, payload: any) =>
                console.log(type, payload)
              }
            />
          ) : (
            <EmptyStateWrapper>
              {getStatusIcon(companyInfo?.status)}
              <EmptyStateText>
                {!accessToken
                  ? t("companyRegistration.emptyText")
                  : t(
                      `companyRegistration.statusDescription.${companyInfo.status}`
                    )}
              </EmptyStateText>
            </EmptyStateWrapper>
          )}
        </MainContentInner>
      </MainContent>
    </div>
  );
}
